import moment from "moment";
import useTranslation from "../../../shared/hooks/useTranslation";
import { BudgetTypeCode, PaymentTermsCode, ServiceRequest, ServiceRequestStatusCode, ServiceTypeCode } from "../models/models";

export const useServiceRequestFormatters = () => {
  const { translate } = useTranslation(["servicing", "myMachinery", "common"]);

  const isPosted = (serviceRequest: ServiceRequest) => {
    return serviceRequest.statusCode === ServiceRequestStatusCode.openForOffers ||
      serviceRequest.statusCode === ServiceRequestStatusCode.negotiating ||
      serviceRequest.statusCode === ServiceRequestStatusCode.inProgress ||
      serviceRequest.statusCode === ServiceRequestStatusCode.completed;
  };

  const formatCreatedDate = (serviceRequest: ServiceRequest) => {
    return moment(serviceRequest.createdDate).format("MMM D, YYYY");
  };

  const formatExpirationDate = (serviceRequest: ServiceRequest) => {
    if (!isPosted(serviceRequest) || !serviceRequest.serviceEndDate) {
      return null;
    }

    const now = moment();
    const endDate = moment(serviceRequest.serviceEndDate);
    if (now >= endDate) {
      return translate("servicing:expired");
    }

    const difference = moment.duration(endDate.diff(now));
    return translate("servicing:expiresIn", { days: difference.days(), hours: difference.hours() });
  };

  const formatPostedDate = (serviceRequest: ServiceRequest) => {
    if (!isPosted(serviceRequest)) {
      return null;
    }

    return `${translate("servicing:posted")} ${moment(serviceRequest.createdDate).format("MMM D, YYYY")}`;
  };

  const formatServiceType = (serviceRequest: ServiceRequest) => {
    const serviceTypes = {
      [ServiceTypeCode.onSiteRepair]: "myMachinery:serviceTypes.onSiteRepair",
      [ServiceTypeCode.maintenance]: "myMachinery:serviceTypes.maintenance",
      [ServiceTypeCode.partsReplacement]: "myMachinery:serviceTypes.partsReplacement",
    };

    return translate(serviceTypes[serviceRequest.serviceTypeCode]);
  };

  const formatServiceDate = (serviceRequest: ServiceRequest) => {
    if (serviceRequest.flexibleDates) {
      return translate("servicing:serviceDateFlexible");
    }

    if (!serviceRequest.serviceStartDate && !serviceRequest.serviceEndDate) {
      return translate("servicing:serviceDateNotSet");
    }

    if (!serviceRequest.serviceStartDate ||
      !serviceRequest.serviceEndDate) {
      return moment(serviceRequest.serviceStartDate || serviceRequest.serviceEndDate).format("MMM D, YYYY");
    }

    const startDate = moment(serviceRequest.serviceStartDate);
    const endDate = moment(serviceRequest.serviceEndDate);
    if (startDate.isSame(endDate, 'day')) {
      return startDate.format("MMM D, YYYY");
    } else if (startDate.isSame(endDate, 'month')) {
      return `${endDate.format("MMM")} ${startDate.format("D")}-${endDate.format("D")}, ${endDate.format("YYYY")}`;
    } else if (startDate.isSame(endDate, 'year')) {
      return `${startDate.format("MMM D")} - ${endDate.format("MMM D, YYYY")}`;
    } else {
      return `${startDate.format("MMM D, YYYY")} - ${endDate.format("MMM D, YYYY")}`;
    }
  };

  const formatWorkingHours = (serviceRequest: ServiceRequest) => {
    if (!serviceRequest.workingHoursFrom || !serviceRequest.workingHoursTo) {
      return translate("servicing:workingHoursNotSet");
    }

    const from = moment(serviceRequest.workingHoursFrom, ["HH:mm:ss"]).format("HH:mm");
    const to = moment(serviceRequest.workingHoursTo, ["HH:mm:ss"]).format("HH:mm")
    return `${from} — ${to}`;
  };

  const formatWeekendWork = (serviceRequest: ServiceRequest) => {
    const weekendWorkKey = serviceRequest.weekendWorkPossible ? "servicing:available" : "servicing:notAvailable";
    return translate(weekendWorkKey);
  };

  const formatBudget = (serviceRequest: ServiceRequest) => {
    if (serviceRequest.budgetTypeCode === BudgetTypeCode.fixed) {
      return `${serviceRequest.fixedBudget}€`;
    }

    if (serviceRequest.budgetTypeCode === BudgetTypeCode.range) {
      return `${serviceRequest.budgetRangeFrom}€ — ${serviceRequest.budgetRangeTo}€`;
    }

    return translate("servicing:budgetTypeCodes.requestQuote");
  };

  const formatPaymentTerms = (serviceRequest: ServiceRequest) => {
    if (!serviceRequest.paymentTermsCode) {
      return translate("servicing:paymentTermsNotSet");
    }

    const paymentTerms = {
      [PaymentTermsCode.net30Days]: "servicing:paymentTermsCodes.net30Days",
      [PaymentTermsCode.net60Days]: "servicing:paymentTermsCodes.net60Days",
      [PaymentTermsCode.net90Days]: "servicing:paymentTermsCodes.net90Days",
      [PaymentTermsCode.dueOnReceipt]: "servicing:paymentTermsCodes.dueOnReceipt",
      [PaymentTermsCode.upfront50Completion]: "servicing:paymentTermsCodes.upfront50Completion",
    };

    return translate(paymentTerms[serviceRequest.paymentTermsCode]);
  };

  return {
    formatCreatedDate,
    formatExpirationDate,
    formatPostedDate,
    formatServiceType,
    formatServiceDate,
    formatWorkingHours,
    formatWeekendWork,
    formatBudget,
    formatPaymentTerms
  };
};
